'use client';

import React, { useState } from 'react';
import { getNavigationForRole, NavPhase, NavItem } from '../../config/navigation';
import styles from './sidebar-navigation.module.css';

interface SidebarNavigationProps {
  user: { Email: string; Name: string; Role: string };
  onLogout?: () => void;
}

export default function SidebarNavigation({ user, onLogout }: SidebarNavigationProps) {
  const phases = getNavigationForRole(user.Role);

  // Determine which phase should be open based on current URL path
  const getCurrentPhaseId = (): string | null => {
    const hash = window.location.hash;
    const currentPath = hash.startsWith('#/') ? hash.substring(2) : hash.substring(1);

    // Find which phase contains the current path
    for (const phase of phases) {
      for (const item of phase.items) {
        if (item.path === currentPath || currentPath.startsWith(item.path + '/')) {
          return phase.id;
        }
      }
    }
    return null;
  };

  const [activePhaseId, setActivePhaseId] = useState<string | null>(getCurrentPhaseId());
  const [userMenuOpen, setUserMenuOpen] = useState(false);

  // Update active phase when URL changes
  React.useEffect(() => {
    const handleHashChange = () => {
      setActivePhaseId(getCurrentPhaseId());
    };
    window.addEventListener('hashchange', handleHashChange);
    return () => window.removeEventListener('hashchange', handleHashChange);
  }, [user.Role]);

  return (
    <aside className={styles.sidebar}>
      {/* Header */}
      <div className={styles.header}>
        <a href="#" className={styles.logoLink}>
          <h1 className={styles.logo}>🌊 FLOW</h1>
        </a>
      </div>

      {/* Navigation Phases */}
      <nav className={styles.navContainer}>
        {phases.map((phase) => (
          <div
            key={phase.id}
            className={`${styles.phase} ${activePhaseId === phase.id ? styles.active : ''}`}
            onClick={() => setActivePhaseId(activePhaseId === phase.id ? null : phase.id)}
          >
            {/* Phase Header */}
            <div className={styles.phaseHeader}>
              <div className={styles.phaseLabel}>{phase.label.toUpperCase()}</div>
              <div className={styles.itemCount}>{phase.items.length}</div>
            </div>

            {/* Phase Items */}
            <div className={styles.itemsList}>
              {phase.items.map((item: any) => (
                <a
                  key={item.id}
                  href={`#${item.path}`}
                  className={`${styles.navItem}`}
                  title={item.label}
                >
                  <span className={styles.itemIcon}>{item.icon}</span>
                  <span className={styles.itemLabel}>{item.label}</span>
                </a>
              ))}
            </div>
          </div>
        ))}
      </nav>

      {/* User Info Footer */}
      <div className={styles.footer}>
        <div className={styles.userMenuContainer}>
          <button
            className={styles.userNameButton}
            onClick={() => setUserMenuOpen(!userMenuOpen)}
          >
            <div className={styles.userNameText}>
              <div className={styles.userName}>{user.Name}</div>
              <div className={styles.userRole}>{user.Role}</div>
            </div>
            <svg
              className={`${styles.chevron} ${userMenuOpen ? styles.open : ''}`}
              width="16"
              height="16"
              viewBox="0 0 16 16"
              fill="none"
            >
              <path d="M4 6L8 10L12 6" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
            </svg>
          </button>

          {userMenuOpen && (
            <div className={styles.userDropdown}>
              <button
                className={styles.logoutItem}
                onClick={() => {
                  setUserMenuOpen(false);
                  onLogout?.();
                }}
              >
                Logout
              </button>
            </div>
          )}
        </div>
      </div>
    </aside>
  );
}
